// 홈(대시보드) 페이지 — 진행 중 / 오늘 예정 / 다음 예정 / 빈 상태 4분기.
//
// 진행 중 일정이 있으면 인라인으로 detail까지 펼쳐서 본인 스케줄 + 전체 라운드별
// 경기 카드까지. 오늘 예정만 있으면 본인 스케줄(스케줄이 생성된 경우)까지.
// D-1이면 "예상 스케줄" 배지로 본인 스케줄을 미리 보여줘서 다음날 준비를
// 돕는 것이 의도.
//
// matchCard는 legacy.ts의 Preact 호스트 함수를 콜백으로 받아 쓰는 가교 구조.
// HTMLElement를 반환하므로 JSX 트리에 끼울 때 DomNode 어댑터로 감싼다.

import { Fragment } from "preact";
import { api } from "../api";
import { canManageEvent, isAdmin, isMember } from "../auth";
import { navigate } from "../routes";
import { state } from "../state";
import { DomNode, mountPreact } from "../ui/preact-mount";
import {
  eventKindLabel,
  formatDateKo,
  formatTimeShort,
  kstTodayStr,
  parseDateParts,
  surfaceLabel,
} from "../ui/format";

export type MatchCardFn = (m: any, ev: any, opts?: any) => HTMLElement;

interface MyScheduleOpts {
  predicted?: boolean;
  showDetailLink?: boolean;
}

function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function MyScheduleSection({
  detail,
  ev,
  opts,
  matchCard,
}: {
  detail: any;
  ev: any;
  opts: MyScheduleOpts;
  matchCard: MatchCardFn;
}) {
  const memberId = state.session?.actor?.member_id;
  if (!memberId) return null;
  const participants = detail.participants || [];
  const matches = detail.matches || [];
  const rounds = detail.rounds || [];
  const courts = detail.courts || [];
  const myP = participants.find((p: any) => p.member_id === memberId);
  if (!myP) return null;
  const myPid = myP.event_participant_id;

  const surfaceMap = new Map<string, string>();
  for (const c of courts) {
    if (c.surface_type) surfaceMap.set(c.event_court_id, surfaceLabel(c.surface_type));
  }
  const roundMap: Record<number, any> = Object.fromEntries(rounds.map((r: any) => [r.round_number, r]));

  const byRound = new Map<number, any[]>();
  for (const m of matches) {
    const rn = m.round_number || 0;
    if (!byRound.has(rn)) byRound.set(rn, []);
    byRound.get(rn)!.push(m);
  }
  const sortedRounds = [...byRound.entries()].sort((a, b) => a[0] - b[0]);
  if (sortedRounds.length === 0) return null;

  return (
    <div class="section">
      <div class="section__title">
        <h3>내 스케줄</h3>
        {opts.predicted && (
          <span class="schedule-predicted-badge">예상 · 변경될 수 있어요</span>
        )}
      </div>
      {sortedRounds.map(([rn, roundMatches]) => {
        const rd = roundMap[rn];
        const timeStr = rd
          ? `${formatTimeShort(rd.scheduled_start_at)} ~ ${formatTimeShort(rd.scheduled_end_at)}`
          : "";
        const myMatch = roundMatches.find((m: any) =>
          (m.sides || []).some((s: any) => (s.player_ids || []).includes(myPid)),
        );
        return (
          <Fragment key={rn}>
            <div class="round-label">
              <span>{rn}R</span>
              {timeStr && <span class="round-label__time">{timeStr}</span>}
            </div>
            {myMatch ? (
              <DomNode node={matchCard(myMatch, ev, { surfaceMap })} />
            ) : (
              <div class="round-rest">🪑 이번 라운드는 휴식</div>
            )}
          </Fragment>
        );
      })}
      {opts.showDetailLink && (
        <button
          class="schedule-detail-link"
          type="button"
          onClick={() => navigate({ name: "event", id: ev.event_id })}
        >
          전체 경기는 일정 상세에서 보기 →
        </button>
      )}
    </div>
  );
}

function UpcomingHero({ ev }: { ev: any }) {
  const parts = parseDateParts(ev.event_date);
  const target = new Date(`${ev.event_date}T00:00:00`);
  const diffDays = Math.round(
    (target.getTime() - new Date(`${kstTodayStr()}T00:00:00`).getTime()) / 86400000,
  );
  const dLabel = diffDays === 0 ? "오늘" : diffDays > 0 ? `D-${diffDays}` : `D+${-diffDays}`;
  return (
    <div
      class="upcoming-next"
      role="button"
      onClick={() => navigate({ name: "event", id: ev.event_id })}
    >
      <div class="upcoming-next__head">
        <span class="upcoming-next__label">다가오는 일정 · {dLabel}</span>
        <span class="upcoming-next__arrow">
          <ChevronIcon size={14} />
        </span>
      </div>
      <p class="upcoming-next__title">{ev.title || "일정"}</p>
      <p class="upcoming-next__meta">
        {parts.month} {parts.day}
        {ev.location ? ` · ${ev.location}` : ""}
      </p>
    </div>
  );
}

function LiveHero({ ev }: { ev: any }) {
  return (
    <div
      class="hero hero--clickable"
      role="button"
      tabIndex={0}
      onClick={() => navigate({ name: "event", id: ev.event_id })}
    >
      <span class="hero__badge">
        <span class="hero__badge-dot" />
        진행 중
      </span>
      <h2 class="hero__title">
        {ev.title || "일정"}
        <span class="hero__title-arrow">
          <ChevronIcon size={16} />
        </span>
      </h2>
      <p class="hero__meta">
        <span>{formatDateKo(ev.event_date)}</span>
        {ev.location && <span>· {ev.location}</span>}
        <span>· {eventKindLabel(ev)}</span>
      </p>
    </div>
  );
}

function TodayHero({ ev }: { ev: any }) {
  const timeStr = ev.court_booking_start_at ? formatTimeShort(ev.court_booking_start_at) : null;
  return (
    <div
      class="hero hero--clickable"
      role="button"
      tabIndex={0}
      onClick={() => navigate({ name: "event", id: ev.event_id })}
    >
      <span class="hero__badge">
        <span class="hero__badge-dot hero__badge-dot--upcoming" />
        {timeStr ? `오늘 ${timeStr} 예정` : "오늘 예정"}
      </span>
      <h2 class="hero__title">
        {ev.title || "일정"}
        <span class="hero__title-arrow">
          <ChevronIcon size={16} />
        </span>
      </h2>
      <p class="hero__meta">
        {ev.location && <span>{ev.location}</span>}
        <span>· {eventKindLabel(ev)}</span>
      </p>
    </div>
  );
}

function EmptyHero() {
  return (
    <div class="hero hero--empty">
      <h2 class="hero__title">오늘은 일정이 없어요</h2>
      <p class="hero__meta">다음 일정이 시작되면 여기에 표시돼요.</p>
    </div>
  );
}

function AllMatchesSection({
  ev,
  matches,
  surfaceMap,
  matchCard,
}: {
  ev: any;
  matches: any[];
  surfaceMap: Map<string, string>;
  matchCard: MatchCardFn;
}) {
  const byRound = new Map<number, any[]>();
  for (const m of matches) {
    const rn = m.round_number || 0;
    if (!byRound.has(rn)) byRound.set(rn, []);
    byRound.get(rn)!.push(m);
  }
  const sorted = [...byRound.entries()].sort((a, b) => a[0] - b[0]);
  return (
    <div class="section">
      <div class="section__title">
        <h3>전체 경기</h3>
        <span class="count">{matches.length}개</span>
      </div>
      {sorted.map(([rn, roundMatches]) => (
        <Fragment key={rn}>
          <p class="round-label">{rn}R</p>
          {roundMatches.map((m: any) => (
            <DomNode key={m.match_id || m.event_match_id} node={matchCard(m, ev, { surfaceMap })} />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

export interface RenderDashboardParams {
  view: HTMLElement;
  matchCard: MatchCardFn;
  onNewEvent: () => void;
}

export async function renderDashboard(params: RenderDashboardParams): Promise<void> {
  const { view, matchCard, onNewEvent } = params;
  view.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "불러오는 중…";
  view.append(loading);

  let events: any[];
  try {
    events = await api("/api/v1/events");
  } catch (e: any) {
    view.innerHTML = "";
    const err = document.createElement("div");
    err.className = "error-card";
    err.textContent = e.message;
    view.append(err);
    return;
  }
  view.innerHTML = "";

  const live = events.find((ev) => ev.status === "in_progress");
  const todayStr = kstTodayStr();
  const todayUpcoming = events
    .filter((ev) => (ev.status === "draft" || ev.status === "ready") && (ev.event_date || "") === todayStr)
    .sort((a, b) => (a.court_booking_start_at || "").localeCompare(b.court_booking_start_at || ""));
  const nextUpcoming = events
    .filter((ev) => (ev.status === "draft" || ev.status === "ready") && (ev.event_date || "") > todayStr)
    .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));

  if (live) {
    view.append(mountPreact(<LiveHero ev={live} />));
    let detail: any;
    try { detail = await api(`/api/v1/events/${live.event_id}`); } catch (_) { detail = null; }
    if (detail) {
      const ev = detail.event || live;
      const matches = detail.matches || [];
      const dCourts = detail.courts || [];
      const dSurfaceMap = new Map<string, string>();
      for (const c of dCourts) {
        if (c.surface_type) dSurfaceMap.set(c.event_court_id, surfaceLabel(c.surface_type));
      }

      view.append(
        mountPreact(
          <MyScheduleSection
            detail={detail}
            ev={ev}
            opts={{ predicted: false, showDetailLink: false }}
            matchCard={matchCard}
          />,
        ),
      );

      if (matches.length > 0 && (isAdmin() || canManageEvent(ev))) {
        view.append(
          mountPreact(
            <AllMatchesSection
              ev={ev}
              matches={matches}
              surfaceMap={dSurfaceMap}
              matchCard={matchCard}
            />,
          ),
        );
      }
    }
  } else if (todayUpcoming.length > 0) {
    const ev = todayUpcoming[0];
    view.append(mountPreact(<TodayHero ev={ev} />));
    let upDetail: any;
    try { upDetail = await api(`/api/v1/events/${ev.event_id}`); } catch (_) { upDetail = null; }
    if (upDetail) {
      view.append(
        mountPreact(
          <MyScheduleSection
            detail={upDetail}
            ev={upDetail.event || ev}
            opts={{ predicted: false, showDetailLink: true }}
            matchCard={matchCard}
          />,
        ),
      );
    }
  } else {
    view.append(mountPreact(<EmptyHero />));

    if (nextUpcoming.length > 0) {
      const nextEv = nextUpcoming[0];
      view.append(mountPreact(<UpcomingHero ev={nextEv} />));

      const nextDate = new Date(`${nextEv.event_date}T00:00:00`);
      const todayDate = new Date(todayStr + "T00:00:00");
      const diffDays = Math.round((nextDate.getTime() - todayDate.getTime()) / 86400000);
      if (diffDays === 1) {
        let nextDetail: any;
        try { nextDetail = await api(`/api/v1/events/${nextEv.event_id}`); } catch (_) { nextDetail = null; }
        if (nextDetail) {
          view.append(
            mountPreact(
              <MyScheduleSection
                detail={nextDetail}
                ev={nextDetail.event || nextEv}
                opts={{ predicted: true, showDetailLink: true }}
                matchCard={matchCard}
              />,
            ),
          );
        }
      }
    } else if (isAdmin() || isMember()) {
      const newBtn = document.createElement("button");
      newBtn.className = "btn btn--accent";
      newBtn.type = "button";
      newBtn.textContent = "＋ 새 일정 만들기";
      newBtn.addEventListener("click", () => onNewEvent());
      view.append(newBtn);
    }
  }
}
